import along from "@turf/along";
import area from "@turf/area";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import buffer from "@turf/buffer";
import { convex } from "@turf/convex";
import { degreesToRadians, feature as asFeature, featureCollection } from "@turf/helpers";
import intersect from "@turf/intersect";
import length from "@turf/length";
import { lineIntersect } from "@turf/line-intersect";
import union from "@turf/union";
import type { Feature, LineString, MultiLineString, MultiPolygon, Polygon } from "geojson";

import {
  areaCentroid,
  holeCount,
  nearestLineDistanceMetres,
  orientedRectangleSidesMetres,
  partCount,
  pointCount,
  polygonPerimeterMetres,
  toLineStrings,
} from "./geometry";
import { splitLineAtPoints } from "./lineSplit";

export type StreetGeometry = LineString | MultiLineString;

export interface ParcelInput {
  /** The geometry of the parcel. **/
  geometry: Polygon | MultiPolygon;

  /** The `AddressType` of the parcel. **/
  addressType: string | null;

  /** Streets near the parcel (excluding service streets) **/
  gateStreets: ReadonlyArray<StreetGeometry>;

  /** All streets near the parcel **/
  nearbyStreets: ReadonlyArray<StreetGeometry>;
}

export interface StreetParcelConfig {
  streetBufferDistanceMetres: number;
}

export const DEFAULT_STREET_PARCEL_CONFIG: StreetParcelConfig = {
  streetBufferDistanceMetres: 10,
};

/**
 * The 'features' of a street parcel that the model considers.
 *
 * The terminology here is somewhat confusing: 'feature' in this context does not mean "geospatial feature";
 * rather, it just means "some attribute or derived value obtained from a parcel".
 */
export interface StreetParcelFeatures {
  // The attributes must align with the features in the model; the model was trained using snake_case,
  // so we align with that naming here.

  /** The area of the parcel. **/
  area_m2: number;

  /**
   * The ratio of the area of buffered streets near the parcel to the area of the parcel itself.
   *
   * This is by far the most powerful feature in terms of detecting street parcels.
   * **/
  street_overlap_ratio: number | null;

  /** The perimeter of the parcel. **/
  perimeter_m: number;

  /**
   * The compactness of the parcel.
   *
   * This is measured using the 'Polsby-Popper' score. Scores range from 0 to 1, with 1 being a perfect circle.
   **/
  compactness: number | null;

  /**
   * The elongation of the parce.
   *
   * This is measured by the ratio: (max side length of OBB)/(min side length of OBB),
   * where OBB is the oriented bounding box of the parcel.
   */
  elongation: number | null;

  /**
   * The width of the parcel.
   *
   * This is measured by the smallest side length of the oriented bounding box of the parcel.
   */
  width_m: number | null;

  /**
   * The convexity of the parcel.
   *
   * This is measured by the ratio of the parcel's area to the area of the parcel's convex hull.
   */
  convexity: number | null;

  /**
   * The number of vertices in the parcel.
   */
  n_points: number;

  /**
   * The number of parts in the parcel.
   *
   * A number greater than one indicates that the parcel's geometry is a MultiPolygon.
   */
  n_parts: number;

  /**
   * The number of 'holes' in the parcel.
   */
  hole_count: number;

  /**
   * The total length of street centerlines within the parcel.
   *
   * Only the portion of the street centerlines contained within the parcel contribute to the length.
   */
  centerline_len_m: number;

  /**
   * The ratio of the street centerline lengths to the perimeter of the parcel.
   */
  centerline_len_per_perim: number | null;

  /**
   * The distance from the parcel centroid to the nearest street, normalized by the square root of the parcel's area
   */
  centroid_street_dist_norm: number | null;

  /** The address type of the parcel. **/
  addresstype: string;
}

/** Return the convexity of the given geometry. **/
function convexity(geom: Polygon | MultiPolygon, areaM2: number): number | null {
  const hull = convex(geom);
  if (!hull) return null;
  const hullArea = area(hull);
  return hullArea > 0 ? areaM2 / hullArea : null;
}

type BufferPolygon = Feature<Polygon | MultiPolygon>;

/** Return the union of the given polygons, or `null` if there are none. */
function unionAll(polygons: BufferPolygon[]): BufferPolygon | null {
  if (polygons.length === 0) return null;

  // union throws if the input has length 1
  return (polygons.length === 1 ? polygons[0] : union(featureCollection(polygons))) ?? null;
}

/** Return the union of the streets' `metres` buffers, or `null` if there are no streets. */
export function unionStreetBuffers(streets: ReadonlyArray<StreetGeometry>, metres: number): BufferPolygon | null {
  return unionAll(streets.map((street) => buffer(street, metres, { units: "meters" })).filter(Boolean));
}

/** Lazily evaluated buffers of a set of streets by a fixed buffer distance. */
export class StreetBuffers {
  private readonly buffers = new Map<StreetGeometry, BufferPolygon | null>();

  constructor(readonly metres: number) {}

  /** Return the buffer of the given street, or `null` if it has none. */
  of(street: StreetGeometry): BufferPolygon | null {
    let buffered = this.buffers.get(street);
    if (buffered === undefined) {
      buffered = buffer(street, this.metres, { units: "meters" }) ?? null;
      this.buffers.set(street, buffered);
    }
    return buffered;
  }

  /** Return the union of the given streets' buffers, or `null` if there are none. */
  union(streets: ReadonlyArray<StreetGeometry>): BufferPolygon | null {
    return unionAll(streets.map((street) => this.of(street)).filter((b): b is BufferPolygon => b !== null));
  }
}

/**
 * Calculate the street overlap ratio of the given parcel geometry.
 *
 * The rough idea is that we buffer each nearby street, union those buffers, intersect the buffer
 * with the parcel, and then measure the area of that intersection. This provides a measure
 * of 'how much of the parcel is close to a street'.
 */
function streetOverlapRatio(
  geom: Polygon | MultiPolygon,
  gateStreets: ReadonlyArray<StreetGeometry>,
  parcelArea: number,
  latitude: number,
  config: StreetParcelConfig
): number | null {
  if (parcelArea <= 0) return null;

  // the model was trained using a 10 "metre" buffer in Web Mercator, so we replicate that buffer here
  const metres = config.streetBufferDistanceMetres * Math.cos(degreesToRadians(latitude));
  const merged = unionStreetBuffers(gateStreets, metres);
  if (!merged) return 0;

  const overlap = intersect(featureCollection([asFeature(geom), merged]));
  return Math.min((overlap ? area(overlap) : 0) / parcelArea, 1);
}

/**
 * Distance from the parcel centroid to the nearest street, normalised by `sqrt(area)`.
 */
function centroidStreetDistNorm(geom: Polygon | MultiPolygon, nearbyStreets: ReadonlyArray<StreetGeometry>, areaM2: number): number | null {
  if (areaM2 <= 0) return null;
  const centroid = areaCentroid(geom);
  if (!centroid) return null;

  const nearest = nearestLineDistanceMetres(centroid.coordinates, nearbyStreets);
  return nearest === null ? null : nearest / Math.sqrt(areaM2);
}

/** Total length of street centrelines inside the parcel. */
function centerlineLengthMetres(geom: Polygon | MultiPolygon, nearbyStreets: ReadonlyArray<StreetGeometry>): number {
  // ideally we could simply intersect each street with the parcel geometry and then compute the sum of the lengths
  // of the resulting (multi)line strings, however turf doesn't appear to support this type of intersection.
  //
  // instead, we cut each linestring where it crosses the parcel boundary. each piece then lies entirely "inside" or
  // "outside" the polygon, which we determine from its midpoint; the "inside" pieces contribute to the total length,
  // while the "outside" pieces are ignored.

  const parcel = asFeature(geom);
  let total = 0;

  for (const street of nearbyStreets) {
    for (const line of toLineStrings(street)) {
      const crossings = lineIntersect(asFeature(line), parcel).features.map((f) => f.geometry.coordinates);
      for (const piece of splitLineAtPoints(line, crossings)) {
        const pieceFeature = asFeature(piece);
        const pieceLengthM = length(pieceFeature, { units: "meters" });
        if (pieceLengthM > 0) {
          const interior = along(pieceFeature, pieceLengthM / 2, { units: "meters" });
          if (booleanPointInPolygon(interior, geom)) total += pieceLengthM;
        }
      }
    }
  }
  return total;
}

/** Compute the model features for a given parcel. **/
export function computeStreetParcelFeatures(parcel: ParcelInput, config: StreetParcelConfig = DEFAULT_STREET_PARCEL_CONFIG): StreetParcelFeatures {
  const { geometry } = parcel;
  const areaM2 = area(geometry);
  const perimeterM = polygonPerimeterMetres(geometry);
  const sides = orientedRectangleSidesMetres(geometry);
  const centroid = areaCentroid(geometry);
  const latitude = centroid ? centroid.coordinates[1] : 0;
  const centerlineLen = centerlineLengthMetres(geometry, parcel.nearbyStreets);

  return {
    area_m2: areaM2,
    street_overlap_ratio: streetOverlapRatio(geometry, parcel.gateStreets, areaM2, latitude, config),
    perimeter_m: perimeterM,
    compactness: perimeterM > 0 ? (4 * Math.PI * areaM2) / perimeterM ** 2 : null,
    elongation: sides ? Math.max(...sides) / Math.min(...sides) : null,
    width_m: sides ? Math.min(...sides) : null,
    convexity: convexity(geometry, areaM2),
    n_points: pointCount(geometry),
    n_parts: partCount(geometry),
    hole_count: holeCount(geometry),
    centerline_len_m: centerlineLen,
    centerline_len_per_perim: perimeterM > 0 ? centerlineLen / perimeterM : null,
    centroid_street_dist_norm: centroidStreetDistNorm(geometry, parcel.nearbyStreets, areaM2),
    addresstype: parcel.addressType ?? "",
  };
}
